// Copyright (c) Docugami, Inc. All rights reserved.

import { ReportEntryProviderItem } from "components/cards/ReportItemsList";
import { addBindingThunk, addEntryThunk } from "components/reports/reportsActions";
import { NamedResource } from "networking/WorkPathApi";
import { ChunkInstance } from "networking/api";
import { ReportBinding, ReportEntry } from "networking/reportsApi";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { AppDispatch, AppState } from "store";
import { ModalSelectionArea, ModalSelectionDisplayContext, ModalSelectionItemType } from "store/documents/types";
import { selectPatchedReportBindingsState, selectPatchedReportSchemaState } from "store/reports/reportsSlice";
import { ModalSelectionError, useModalSelection } from "./ModalSelection";
import { useChunkSelection } from "./chunkSelection";
import { useAppDispatch, useAppSelector } from "./redux";
import { selectEntries, selectReportDocumentId, selectReportEntryBindingsForCurrentDocument, selectReportId } from "store/reports/selectors";
import { createFolderId } from "components/file_management/file_helpers";

export interface IChunkLinking {
    entryId?: string;
    spanId?: string;

    isLinking: boolean;
    isLinkingEntry: boolean;
    isLinkingChunk: boolean;

    startLinkingChunk: (chunk: ChunkInstance, autoLink?: boolean, dropTarget?: boolean) => void;
    startLinkingEntry: (entry: ReportEntry, autoLink?: boolean) => void;

    canLinkChunk: (chunk: ChunkInstance) => boolean;
    canLinkEntry: (entry: ReportEntry) => boolean;

    cancel: () => void;

    errorModal: JSX.Element | null;
}

export const noOpChunkLinking: IChunkLinking = {
    isLinking: false,
    isLinkingEntry: false,
    isLinkingChunk: false,
    startLinkingChunk: () => { },
    startLinkingEntry: () => { },
    canLinkChunk: () => false,
    canLinkEntry: () => false,
    cancel: () => { },
    errorModal: null
}

interface ChunkLinkingState {
    entry?: ReportEntry;
    chunk?: ChunkInstance;
}

export const chunkInstanceIsBound = (chunkInstance: ChunkInstance) => (dispatch: AppDispatch, getState: () => AppState): boolean => {
    const state = getState();
    const context = state.reportsSlice.context;
    if (!context) {
        return false;
    }

    const reportId = state.workPaths.currentWorkPath?.resources[NamedResource.ReportId];
    if (!reportId) {
        return false;
    }

    const entryIds = selectPatchedReportSchemaState(state, reportId)?.report.entries;
    const { fileId } = createFolderId(chunkInstance.folderId);
    const allEntryBindings = selectPatchedReportBindingsState(state, { reportId: reportId, fileId: fileId })?.entryBindings;
    if (!allEntryBindings || !entryIds) {
        return false;
    }

    for (const entryId of entryIds) {
        const bindings = allEntryBindings.entities[entryId]?.bindings;
        if (!!bindings?.find(x => !!x.spanId && x.spanId === chunkInstance.spanId)) {
            return true;
        }
    }

    return false;
}

export function useChunkLinking(): IChunkLinking {
    const [state, setState] = useState<ChunkLinkingState>({});

    const dispatch = useAppDispatch();

    const isExternalModalActive = useSelector((state: AppState) => state.documents.modalSelectionContext.isActive);
    const { selectChunk, clearSelection } = useChunkSelection();

    const { start: requestChunkStart, stop: requestChunkStop, isActive: isLinkingChunk, errorModal: chunkErrorModal } = useModalSelection<ChunkInstance>(onChunkInstanceConfirmed, onCancel);
    const { start: requestEntryStart, stop: requestEntryStop, isActive: isLinkingEntry, errorModal: entryErrorModal } = useModalSelection<ReportEntryProviderItem>(onEntryConfirmed, onCancel);

    const folderId = useAppSelector(selectReportDocumentId);
    const documentBindings = useAppSelector(selectReportEntryBindingsForCurrentDocument);
    const reportId = useAppSelector(selectReportId);
    const entries = useAppSelector(selectEntries);

    function onCancel() {
        cancel();
    }

    function onChunkInstanceConfirmed(chunk?: ChunkInstance): ModalSelectionError | void {
        if (!chunk || !state.entry || !reportId) {
            cancel();
            return;
        }

        const errorMessage = getValidatedBindingErrorMessage(chunk, documentBindings?.entities[state.entry.id]?.bindings);
        if (!!errorMessage) {
            const error: ModalSelectionError = {
                title: "Linking Error",
                message: errorMessage
            }

            setState({});
            return error;
        }

        const instanceId = chunk.spanId!;
        selectChunk(instanceId);

        if (!!entries?.entities[state.entry.id]) {
            dispatch(addBindingThunk(reportId, {
                folderId: folderId,
                entryId: state.entry.id,
                instanceId: instanceId
            }))
        }
        else {
            dispatch(addEntryThunk(reportId, {
                folderId: folderId,
                instanceId: instanceId
            }))
        }

        setState({});
    }

    function onEntryConfirmed(item?: ReportEntryProviderItem): ModalSelectionError | void {
        const entry = item?.entry;
        if (!entry || !state.chunk || !reportId) {
            cancel();
            return;
        }

        const errorMessage = getValidatedBindingErrorMessage(state.chunk, documentBindings?.entities[entry.id]?.bindings);
        if (!!errorMessage) {
            const error: ModalSelectionError = {
                title: "Linking Error",
                message: errorMessage
            }

            setState({});
            return error;
        }

        const instanceId = state.chunk.spanId!;
        selectChunk(instanceId);

        dispatch(addBindingThunk(reportId, {
            folderId: folderId,
            entryId: entry.id,
            instanceId: instanceId,
            prevInstanceId: item?.instanceId
        }))

        setState({});
    }

    const startLinkingEntry = useCallback((entry: ReportEntry, autoLink?: boolean): void => {
        if (isExternalModalActive) {
            return;
        }

        setState(x => { return { ...x, entry: entry } });

        const label = entry.title;

        const displayContext: ModalSelectionDisplayContext = {
            title: "Linking Mode",
            defaultMessage: !!label ? `Select or create a chunk to associate with {0}` : `Select or create a chunk to create a new item`,
            selectedMessage: !!label ? "Confirm this chunk as {0}" : "Confirm this chunk",
            formatArgs: [label]
        }

        requestChunkStart(ModalSelectionItemType.Chunk, displayContext, autoLink, [ModalSelectionArea.Canvas, ModalSelectionArea.ReportItems]);
    }, [requestChunkStart, isExternalModalActive]);

    const startLinkingChunk = useCallback((chunk: ChunkInstance, autoLink?: boolean, dropTarget?: boolean): void => {
        if (isExternalModalActive) {
            return;
        }

        setState(x => { return { ...x, chunk: chunk } });

        const displayContext: ModalSelectionDisplayContext = {
            title: "Linking Mode",
            defaultMessage: "Link this chunk to one of your items on the right."
        };

        requestEntryStart(ModalSelectionItemType.Entry, displayContext, autoLink, [ModalSelectionArea.Canvas, ModalSelectionArea.ReportItems], dropTarget);
    }, [requestEntryStart, isExternalModalActive]);

    const canLinkChunk = useCallback((chunk: ChunkInstance): boolean => {
        if (!!state.chunk || !reportId) {
            return false;
        }

        if (!entries?.ids.length) {
            return false;
        }

        return !dispatch(chunkInstanceIsBound(chunk));
    }, [state, entries?.ids.length, reportId, dispatch]);

    const canLinkEntry = useCallback((entry: ReportEntry): boolean => {
        if (!!state.entry || !reportId) {
            return false;
        }

        return true;
    }, [state, reportId]);

    const cancel = useCallback((): void => {
        if (isLinkingChunk) {
            clearSelection();
            requestChunkStop();
        }

        if (isLinkingEntry) {
            requestEntryStop();
        }

        setState({});
    }, [isLinkingChunk, isLinkingEntry, requestChunkStop, requestEntryStop, clearSelection]);

    if (!reportId) {
        return noOpChunkLinking;
    }

    return {
        entryId: state.entry?.id,
        spanId: state.chunk?.spanId,

        isLinking: isLinkingChunk || isLinkingEntry,
        isLinkingChunk: isLinkingChunk,
        isLinkingEntry: isLinkingEntry,

        startLinkingChunk: startLinkingChunk,
        startLinkingEntry: startLinkingEntry,

        canLinkChunk: canLinkChunk,
        canLinkEntry: canLinkEntry,

        cancel: cancel,

        errorModal: isLinkingEntry ? entryErrorModal : chunkErrorModal
    };
}

export function validateBinding(chunk: ChunkInstance, bindings: ReportBinding[] | undefined): boolean {
    return !getValidatedBindingErrorMessage(chunk, bindings);
}

export function getValidatedBindingErrorMessage(chunk: ChunkInstance, bindings: ReportBinding[] | undefined): string | null {
    if (!!bindings?.find(x => x.spanId === chunk.chunkInstanceId)) {
        return 'This chunk is already linked to this item.';
    }

    return null;
}
